'use client';

import {
  createContext,
  useContext,
  useCallback,
  useMemo,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import type { ParsedEndpoint } from '../components/ToolCallsRenderers/types';

type ChatEndpointsContextType = {
  fetchEndpoints: () => Promise<ParsedEndpoint[]>;
};

const defaultFetchEndpoints = async (): Promise<ParsedEndpoint[]> => [];

const ChatEndpointsContext = createContext<ChatEndpointsContextType>({
  fetchEndpoints: defaultFetchEndpoints,
});

type ChatEndpointsProviderProps = {
  children: ReactNode;
  fetchEndpoints: () => Promise<ParsedEndpoint[]>;
};

export function ChatEndpointsProvider({
  children,
  fetchEndpoints,
}: ChatEndpointsProviderProps) {
  const cacheRef = useRef<ParsedEndpoint[] | null>(null);
  const pendingRef = useRef<Promise<ParsedEndpoint[]> | null>(null);

  useEffect(() => {
    cacheRef.current = null;
    pendingRef.current = null;
  }, [fetchEndpoints]);

  const cachedFetchEndpoints = useCallback(async (): Promise<
    ParsedEndpoint[]
  > => {
    if (cacheRef.current !== null) {
      return cacheRef.current;
    }

    if (pendingRef.current !== null) {
      return pendingRef.current;
    }

    const promise = fetchEndpoints().then(
      (endpoints) => {
        cacheRef.current = endpoints;
        pendingRef.current = null;
        return endpoints;
      },
      (error: unknown) => {
        pendingRef.current = null;
        throw error;
      },
    );

    pendingRef.current = promise;
    return promise;
  }, [fetchEndpoints]);

  const contextValue = useMemo(
    () => ({ fetchEndpoints: cachedFetchEndpoints }),
    [cachedFetchEndpoints],
  );

  return (
    <ChatEndpointsContext.Provider value={contextValue}>
      {children}
    </ChatEndpointsContext.Provider>
  );
}

export function useChatEndpoints(): ChatEndpointsContextType {
  return useContext(ChatEndpointsContext);
}
